
import React, { useEffect, useState } from "react";
import { toast } from "react-hot-toast";
import Select from "react-select";
import { axiosInstance } from "../../utility/apiInstance";
import { useRole } from "../MangeContextProvide";
import OceanShippingShowModal from "./ShowOceanShipping";
import OceanDialogBox from "../../ocean/oceanPage/oceantrackedlist/OceanDialogBox";
import { DialogTitle, Typography } from "@mui/material";
import ModalPopup from "../../common/ModalPopup";
import AddcustomfieldsForm from "../AddcustomfieldsForm";
import MilestoneGroupingAccordionBody from "./MilestoneGroupingAccordion";
import AlertConfigAccordionBody, {
  AlertConfig,
  EMPTY_ALERT_CONFIG,
} from "./AlertConfigAccordionBody";
import { FieldData } from "./customFieltType";
import { convertArray } from "./customFieldtConvert";
import CustomFieldsPanel, { buildEmptyRow, FieldRowEntry } from "./CustomFieldsPanel";
import "./AlertConfigAccordionBody.css";

// ── Types ──────────────────────────────────────────────────────────────────────

interface ShippingLine {
  _id: string;
  liner: string;
  scacCode: string;
  abbreviation: string;
}

export interface TrackingEntry {
  trackingNumber: string;
  customFields: FieldData[];
  customerName?: any;
  groupMilestone?: any;
  alertConfig?: AlertConfig;        // ← added
}

export interface CfAccordionEntry {
  id: string;
  identifier: string;
  fieldName: string;
  fields: FieldData[];
  customerName?: any;
  groupMilestone?: any;
  alertConfig?: AlertConfig;        // ← added
}

interface OceanModalState {
  subcriptionPopup: boolean;
  mileStonePopup: boolean;
  groupmilestoneModal: boolean;
  customfieldpopup: boolean;
  trackingCfPopup: boolean;
  cfTabPopup: boolean;
  perEntryGroupingPopup: boolean;
  perEntryAlertPopup: boolean;      // ← added
}

type InputMode = "tracking" | "customFields";

// ── Helpers ────────────────────────────────────────────────────────────────────

function buildFieldstype(allField: any[]) {
  return allField.map((item, index) => {
    let f_type: "Textbox" | "select" | "Date" = "Textbox";
    if (item.fieldType === "Dropdown List" || item.fieldType === "Yes/No")
      f_type = "select";
    if (item.fieldType === "Date") f_type = "Date";
    const options =
      item.fieldType === "Dropdown List"
        ? item.dropdownOptions.map((o: string, i: number) => ({ id: i + 1, label: o }))
        : item.fieldType === "Yes/No"
          ? [{ id: 1, label: "Yes" }, { id: 2, label: "No" }]
          : [];
    return {
      id: index + 1,
      f_name: item.fieldName,
      f_type,
      default_value: "",
      is_required: item.required ? 1 : 0,
      options,
    };
  });
}

export function buildBlankFieldSet(
  allField: any[],
  uniqueSetKey: number | string
): FieldData[] {
  return allField.map((item: any, i: number) => ({
    id: `${uniqueSetKey}_${i + 1}`,
    autocompleteValue: {
      id: i + 1,
      label: item.fieldName,
      required: item.required,
      customFieldId: item._id,
      isUnique: item.isUnique,
    },
    textValue: "",
    label: item.fieldName,
    value: "",
    required: item.required,
    customFieldId: item._id,
    isUnique: item.isUnique,
  }));
}

function reconcileEntries(
  newTrackingList: string[],
  prevEntries: TrackingEntry[],
  allField: any[]
): TrackingEntry[] {
  const existingMap = new Map(prevEntries.map((e) => [e.trackingNumber, e]));
  return newTrackingList.map((num, idx) => {
    if (existingMap.has(num)) return existingMap.get(num)!;
    const sameIndexEntry = prevEntries[idx];
    if (sameIndexEntry) return { ...sameIndexEntry, trackingNumber: num };
    return {
      trackingNumber: num,
      customFields: buildBlankFieldSet(allField, `${num}_${idx}_${Date.now()}`),
    };
  });
}

function applyPrefill(
  blankFields: FieldData[],
  prefillSet: FieldData[],
  forceUpdate = false
): FieldData[] {
  if (!prefillSet || prefillSet.length === 0) return blankFields;
  const prefillMap = new Map<string, FieldData["value"]>(
    prefillSet
      .filter((f) => f.customFieldId != null && f.customFieldId !== "" && f.value !== "" && f.value !== undefined)
      .map((f) => [f.customFieldId as string, f.value])
  );
  if (prefillMap.size === 0) return blankFields;
  return blankFields.map((field) => {
    if (!forceUpdate && field.value !== "" && field.value !== undefined) return field;
    if (!field.customFieldId) return field;
    const prefilled = prefillMap.get(field.customFieldId);
    if (prefilled !== undefined) {
      return { ...field, value: prefilled, textValue: Array.isArray(prefilled) ? "" : (prefilled as string) };
    }
    return field;
  });
}

type UniqueFilledField = FieldData & { value: string; label: string; customFieldId: string; };

function buildCfAccordionEntries(
  fieldSets: FieldData[][],
  allField: any[],
  prevEntries: CfAccordionEntry[]
): CfAccordionEntry[] {
  const prevMap = new Map(prevEntries.map((e) => [e.id, e]));
  const uniqueFilledFields: UniqueFilledField[] = [];

  for (const set of fieldSets) {
    for (const field of set) {
      if (
        field.isUnique === true &&
        typeof field.value === "string" && field.value.trim() !== "" &&
        typeof field.label === "string" && field.label !== "" &&
        typeof field.customFieldId === "string" && field.customFieldId !== ""
      ) {
        uniqueFilledFields.push(field as UniqueFilledField);
      }
    }
  }

  return uniqueFilledFields.map((uniqueField) => {
    const entryId = `cfacc_${uniqueField.id}`;
    const existing = prevMap.get(entryId);
    if (existing) {
      return {
        ...existing,
        identifier: uniqueField.value,
        fieldName: uniqueField.label,
        fields: existing.fields.map((f) =>
          f.customFieldId === uniqueField.customFieldId
            ? { ...f, value: uniqueField.value, textValue: uniqueField.value, prefilled: true }
            : f
        ),
      };
    }
    const blankFields = buildBlankFieldSet(allField, `${entryId}_${Date.now()}`);
    const markedFields = blankFields.map((f) =>
      f.customFieldId === uniqueField.customFieldId ? { ...f, prefilled: true } : f
    );
    return {
      id: entryId,
      identifier: uniqueField.value,
      fieldName: uniqueField.label,
      fields: applyPrefill(markedFields, [uniqueField]),
    };
  });
}

const emptySubscriptionForm = {
  whatsApp: [],
  emails: [],
  whatsAppMilestones: [],
  emailMilestones: [],
  mileStoneType: "",
  whatsAppgroups: [],
  whatsappGroupMilestones: [],
  groupMilestone: { _id: "" },
  flags: [],
  customerName: null,
};

// ── Component ──────────────────────────────────────────────────────────────────

export default function OceanTrackingPanel({ setShowAirBulk, activeTab, drawerClose }: any) {
  const { chargeRate, availableCredit } = useRole() as any;

  const [allField, setAllField] = useState<any[]>([]);

  // ── Tracking tab ──────────────────────────────────────────────────────────
  const [oceanNum, setOceanNum] = useState("");
  const [trackingEntries, setTrackingEntries] = useState<TrackingEntry[]>([]);
  const [openAccordion, setOpenAccordion] = useState<number | null>(0);
  const [entryFieldErrors, setEntryFieldErrors] = useState<Record<number, Record<string, string>>>({});
  const [entryErrors, setEntryErrors] = useState<Record<number, Record<string, { autocomplete: boolean; text: boolean }>>>({});

  // ── Custom Fields tab — panel ─────────────────────────────────────────────
  const [fieldSets, setFieldSets] = useState<FieldData[][]>([]);
  const [errors, setErrors] = useState<Record<number, Record<string, { autocomplete: boolean; text: boolean }>>>({});
  const [fieldErrors, setFieldErrors] = useState<Record<number, Record<string, string>>>({});
  const [dataArray, setDataArray] = useState<FieldData[]>([]);
  const [setCounter, setSetCounter] = useState(1);

  // ── Lifted panel row state ────────────────────────────────────────────────
  const [cfRows, setCfRows] = useState<FieldRowEntry[]>([buildEmptyRow(`row_${Date.now()}`)]);
  const [cfUniqueErrors, setCfUniqueErrors] = useState<Record<string, string>>({});
  const [cfValidating, setCfValidating] = useState<Record<string, boolean>>({});

  // ── Custom Fields tab — accordion popup ───────────────────────────────────
  const [cfAccordionEntries, setCfAccordionEntries] = useState<CfAccordionEntry[]>([]);
  const [cfOpenAccordion, setCfOpenAccordion] = useState<number | null>(0);
  const [cfEntryFieldErrors, setCfEntryFieldErrors] = useState<Record<number, Record<string, string>>>({});
  const [cfEntryErrors, setCfEntryErrors] = useState<Record<number, Record<string, { autocomplete: boolean; text: boolean }>>>({});

  // ── Per-entry milestone grouping state ────────────────────────────────────
  const [applyAllMilestone, setApplyAllMilestone] = useState<any>(null);
  const [groupingOpenAccordion, setGroupingOpenAccordion] = useState<number | null>(0);

  // ── Per-entry alert config state ──────────────────────────────────────────  ← added
  const [applyAllAlert, setApplyAllAlert] = useState<AlertConfig | null>(null);
  const [alertOpenAccordion, setAlertOpenAccordion] = useState<number | null>(0);
  const [sharedContactList, setSharedContactList] = useState<any[]>([]);
  const [sharedTemplateList, setSharedTemplateList] = useState<any[]>([]);
  const [sharedOrgList, setSharedOrgList] = useState<any[]>([]);

  // ── Shared ────────────────────────────────────────────────────────────────
  const [shippingLines, setShippingLines] = useState<ShippingLine[]>([]);
  const [oceanLinesModal, setOceanLinesModal] = useState(false);
  const [oceanShow, setOceanShow] = useState(false);
  const [canClosedrawer, setCanCloseDrawer] = useState(false);
  const [oceanErrorList, setOceanErrorList] = useState<any>(null);
  const [oceanStatusMsg, setOceanStatusMsg] = useState<any>({});
  const [oceanSubData, setOceanSubData] = useState<any[]>([]);
  const [oceanSubForm, setOceanSubForm] = useState<any>({ ...emptySubscriptionForm });
  const [isSubmitting, setIsSubmitting] = useState(false);

  const [oceanModal, setOceanModal] = useState<OceanModalState>({
    subcriptionPopup: false,
    mileStonePopup: false,
    groupmilestoneModal: false,
    customfieldpopup: false,
    trackingCfPopup: false,
    cfTabPopup: false,
    perEntryGroupingPopup: false,
    perEntryAlertPopup: false,      // ← added
  });

  const [inputMode, setInputMode] = useState<InputMode>("tracking");

  const isLowOcean = availableCredit <= 0 || availableCredit < chargeRate?.containerRate;
  const Fieldstype = buildFieldstype(allField);
  const simpleOptions = allField.map((item, i) => ({
    id: i + 1,
    label: item.fieldName,
    required: item.required,
  }));

  // ── Data fetching ─────────────────────────────────────────────────────────

  const getCustomForms = async () => {
    try {
      const { data } = await axiosInstance.get("/api/v1/custom-form/admin-fields");
      const custumRequiredField = data.data.filter((ele: any) => ele.required);
      setAllField(custumRequiredField);
      setFieldSets([buildBlankFieldSet(custumRequiredField, 0)]);
      setSetCounter(1);
    } catch (_) { }
  };

  const fetchOceanData = async () => {
    try {
      const [msRes, wgRes, flRes, slRes, gmRes] = await Promise.all([
        axiosInstance.get("/api/v1/ocean/ocean-milestone"),
        axiosInstance.get("/api/v1/whatsapp-group"),
        axiosInstance.get("/api/v1/flags"),
        axiosInstance.get("/api/v1/ocean/shippingline"),
        axiosInstance.get("/api/v1/groupingMileStone?type=ocean"),
      ]);
      setOceanSubForm((p: any) => ({
        ...p,
        milestoneList: msRes.data?.data,
        whatsappGroupList: wgRes.data?.data,
        flagsList: flRes.data?.data || [],
        groupingmilestonesList: gmRes.data?.data || [],
      }));
      if (slRes.status === 200 && slRes.data.success) setShippingLines(slRes.data.data);
    } catch (_) { }
  };

  // ← added: fetch contacts, templates, orgs for alert config
  const fetchSharedContacts = async () => {
    try {
      const [contactRes, tplRes, orgRes] = await Promise.all([
        axiosInstance.get("/api/v1/contact"),
        axiosInstance.get("/api/v1/ocean/template"),
        axiosInstance.get("/api/v1/master-contact-organization"),
      ]);
      setSharedContactList(contactRes.data?.data || []);
      setSharedTemplateList(tplRes.data?.data || []);
      setSharedOrgList(orgRes.data?.data || []);
    } catch (_) {
      setSharedContactList([]);
      setSharedTemplateList([]);
      setSharedOrgList([]);
    }
  };

  useEffect(() => {
    getCustomForms();
    fetchOceanData();
    fetchSharedContacts();   // ← added
  }, []);

  // ── Tab switch ────────────────────────────────────────────────────────────

  const handleModeSwitch = (mode: InputMode) => {
    if (mode === inputMode) return;
    setOceanNum("");
    setTrackingEntries([]);
    setEntryFieldErrors({});
    setEntryErrors({});
    setOpenAccordion(0);
    setCfAccordionEntries([]);
    setCfOpenAccordion(0);
    setCfEntryFieldErrors({});
    setCfEntryErrors({});
    setErrors({});
    setFieldErrors({});
    setOceanSubForm((prev: any) => ({ ...prev, ...emptySubscriptionForm }));
    setCfRows([buildEmptyRow(`row_${Date.now()}`)]);
    setCfUniqueErrors({});
    setCfValidating({});
    setFieldSets([buildBlankFieldSet(allField, Date.now())]);
    setSetCounter((c) => c + 1);
    setApplyAllMilestone(null);
    setApplyAllAlert(null);         // ← added
    setInputMode(mode);
  };

  // ── Ocean input handler ───────────────────────────────────────────────────

  const handleOceanInput = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
    let v = e.target.value
      .replace(/[\r\n]+/g, ",")
      .replace(/\s*,\s*/g, ",")
      .replace(/\s+/g, ",")
      .replace(/[^A-Z0-9,-]/gi, "")
      .toUpperCase()
      .trim();
    v = v
      .split(",")
      .map((num) => {
        num = num.trim();
        if (!num) return "";
        if (!num.includes("-") && num.length > 7) {
          const suffix = num.slice(-4);
          if (/^[A-Z]{4}$/.test(suffix)) return `${num.slice(0, -4)}-${suffix}`;
        }
        return num;
      })
      .filter((a) => a !== undefined)
      .join(",");

    setOceanNum(v);

    const trackingList = v.split(",").map((a) => a.trim()).filter(Boolean);
    if (trackingList.length === 0) {
      setTrackingEntries([]);
    } else {
      setTrackingEntries((prev) => reconcileEntries(trackingList, prev, allField));
    }
    setEntryFieldErrors({});
    setEntryErrors({});
  };

  // ── Per-entry state setters — tracking tab ────────────────────────────────

  const makeSetFields = (entryIdx: number) => (updater: React.SetStateAction<FieldData[]>) => {
    setTrackingEntries((prev) =>
      prev.map((entry, i) => {
        if (i !== entryIdx) return entry;
        const next = typeof updater === "function" ? updater(entry.customFields) : updater;
        return { ...entry, customFields: next };
      })
    );
  };

  const makeSetEntryFieldErrors = (entryIdx: number) => (updater: React.SetStateAction<Record<string, string>>) => {
    setEntryFieldErrors((prev) => {
      const current = prev[entryIdx] ?? {};
      const next = typeof updater === "function" ? updater(current) : updater;
      return { ...prev, [entryIdx]: next };
    });
  };

  const makeSetEntryErrors = (entryIdx: number) => (updater: React.SetStateAction<any>) => {
    setEntryErrors((prev) => {
      const current = prev[entryIdx] ?? {};
      const next = typeof updater === "function" ? updater(current) : updater;
      return { ...prev, [entryIdx]: next };
    });
  };

  // ── Per-entry state setters — CF accordion popup ──────────────────────────

  const makeCfSetFields = (entryIdx: number) => (updater: React.SetStateAction<FieldData[]>) => {
    setCfAccordionEntries((prev) =>
      prev.map((entry, i) => {
        if (i !== entryIdx) return entry;
        const next = typeof updater === "function" ? updater(entry.fields) : updater;
        return { ...entry, fields: next };
      })
    );
  };

  const makeCfEntryFieldErrors = (entryIdx: number) => (updater: React.SetStateAction<Record<string, string>>) => {
    setCfEntryFieldErrors((prev) => {
      const current = prev[entryIdx] ?? {};
      const next = typeof updater === "function" ? updater(current) : updater;
      return { ...prev, [entryIdx]: next };
    });
  };

  const makeCfEntryErrors = (entryIdx: number) => (updater: React.SetStateAction<any>) => {
    setCfEntryErrors((prev) => {
      const current = prev[entryIdx] ?? {};
      const next = typeof updater === "function" ? updater(current) : updater;
      return { ...prev, [entryIdx]: next };
    });
  };

  // ── Per-entry customerName setters ────────────────────────────────────────

  const makeSetCustomerName = (entryIdx: number) => (option: any) => {
    setTrackingEntries((prev) =>
      prev.map((entry, i) => {
        if (i === entryIdx) return { ...entry, customerName: option };
        if (entryIdx === 0 && !entry.customerName) return { ...entry, customerName: option };
        return entry;
      })
    );
  };

  const makeCfSetCustomerName = (entryIdx: number) => (option: any) => {
    setCfAccordionEntries((prev) =>
      prev.map((entry, i) => {
        if (i === entryIdx) return { ...entry, customerName: option };
        if (entryIdx === 0 && !entry.customerName) return { ...entry, customerName: option };
        return entry;
      })
    );
  };

  // ── Per-entry milestone grouping handlers ─────────────────────────────────

  const makeSetGroupMilestone = (entryIdx: number) => (option: any) => {
    setApplyAllMilestone(null);
    setTrackingEntries((prev) =>
      prev.map((entry, i) => i === entryIdx ? { ...entry, groupMilestone: option } : entry)
    );
  };

  const makeCfSetGroupMilestone = (entryIdx: number) => (option: any) => {
    setApplyAllMilestone(null);
    setCfAccordionEntries((prev) =>
      prev.map((entry, i) => i === entryIdx ? { ...entry, groupMilestone: option } : entry)
    );
  };

  const handleApplyAllMilestone = (option: any) => {
    setApplyAllMilestone(option);
    if (inputMode === "tracking") {
      setTrackingEntries((prev) => prev.map((entry) => ({ ...entry, groupMilestone: option })));
    } else {
      setCfAccordionEntries((prev) => prev.map((entry) => ({ ...entry, groupMilestone: option })));
    }
  };

  const hasUnique = fieldSets.some((set) =>
    set.some((f) => f.isUnique && f.value !== "" && f.value !== undefined)
  );



  // ── KEY FIX: Re-sync cfAccordionEntries whenever fieldSets changes ────────
  // This ensures that edits made in CustomFieldsPanel (changing the unique
  // field value) are immediately reflected in cfAccordionEntries, so that
  // trackingEntries in the payload always has the latest value.
  useEffect(() => {
    if (inputMode !== "customFields") return;

    if (!hasUnique) {
      setCfAccordionEntries([]);
      return;
    }

    setCfAccordionEntries((prev) =>
      buildCfAccordionEntries(fieldSets, allField, prev)
    );
  }, [fieldSets, inputMode]);
  const openPerEntryGroupingPopup = () => {
    if (inputMode === "tracking") {
      if (!oceanNum.trim()) {
        toast.error("Please enter at least one tracking number first.");
        return;
      }
    } else {
      if (!hasUnique) {
        toast.error("Please fill at least one Unique custom field first.");
        return;
      }
      const entries = buildCfAccordionEntries(fieldSets, allField, cfAccordionEntries);
      setCfAccordionEntries(entries);
    }
    setApplyAllMilestone(null);
    setGroupingOpenAccordion(0);
    setOceanModal((p) => ({ ...p, perEntryGroupingPopup: true }));
  };

  // ── Per-entry alert config handlers ──────────────────────────────────────  ← added block

  const makeSetAlertConfig = (entryIdx: number) => (cfg: AlertConfig) => {
    setApplyAllAlert(null);
    setTrackingEntries((prev) =>
      prev.map((entry, i) => i === entryIdx ? { ...entry, alertConfig: cfg } : entry)
    );
  };

  const makeCfSetAlertConfig = (entryIdx: number) => (cfg: AlertConfig) => {
    setApplyAllAlert(null);
    setCfAccordionEntries((prev) =>
      prev.map((entry, i) => i === entryIdx ? { ...entry, alertConfig: cfg } : entry)
    );
  };

  const handleApplyAllAlert = (cfg: AlertConfig) => {
    setApplyAllAlert(cfg);
    if (inputMode === "tracking") {
      setTrackingEntries((prev) => prev.map((entry) => ({ ...entry, alertConfig: cfg })));
    } else {
      setCfAccordionEntries((prev) => prev.map((entry) => ({ ...entry, alertConfig: cfg })));
    }
  };

  const openPerEntryAlertPopup = () => {
    if (inputMode === "tracking") {
      if (!oceanNum.trim()) {
        toast.error("Please enter at least one tracking number first.");
        return;
      }
    } else {
      if (!hasUnique) {
        toast.error("Please fill at least one Unique custom field first.");
        return;
      }
      const entries = buildCfAccordionEntries(fieldSets, allField, cfAccordionEntries);
      setCfAccordionEntries(entries);
    }
    setApplyAllAlert(null);
    setAlertOpenAccordion(0);
    setOceanModal((p) => ({ ...p, perEntryAlertPopup: true }));
  };

  // ── Validation ────────────────────────────────────────────────────────────

  const validateEntries = (): boolean => {
    const newEntryFieldErrors: Record<number, Record<string, string>> = {};
    let hasError = false;
    let firstErrorIdx: number | null = null;
    trackingEntries.forEach((entry, entryIdx) => {
      entry.customFields.forEach((field) => {
        if (!field.required) return;
        const isEmpty = field.value === "" || field.value === undefined || (Array.isArray(field.value) && field.value.length === 0);
        if (isEmpty) {
          if (!newEntryFieldErrors[entryIdx]) newEntryFieldErrors[entryIdx] = {};
          newEntryFieldErrors[entryIdx][field.id] = `${field.label} is required`;
          hasError = true;
          if (firstErrorIdx === null) firstErrorIdx = entryIdx;
        }
      });
    });
    if (hasError) {
      setEntryFieldErrors(newEntryFieldErrors);
      if (firstErrorIdx !== null) setOpenAccordion(firstErrorIdx);
      return false;
    }
    return true;
  };

  const validateCfEntries = (): boolean => {
    const newErrors: Record<number, Record<string, string>> = {};
    let hasError = false;
    let firstErrorIdx: number | null = null;
    cfAccordionEntries.forEach((entry, entryIdx) => {
      entry.fields.forEach((field) => {
        if (!field.required) return;
        const isEmpty = field.value === "" || field.value === undefined || (Array.isArray(field.value) && field.value.length === 0);
        if (isEmpty) {
          if (!newErrors[entryIdx]) newErrors[entryIdx] = {};
          newErrors[entryIdx][field.id] = `${field.label} is required`;
          hasError = true;
          if (firstErrorIdx === null) firstErrorIdx = entryIdx;
        }
      });
    });
    if (hasError) {
      setCfEntryFieldErrors(newErrors);
      if (firstErrorIdx !== null) setCfOpenAccordion(firstErrorIdx);
      return false;
    }
    return true;
  };

  const validateAllSets = (): boolean => {
    const newFieldErrors: Record<number, Record<string, string>> = {};
    let hasError = false;
    fieldSets.forEach((set, posIdx) => {
      set.forEach((field) => {
        if (!field.required) return;
        const isEmpty = field.value === "" || field.value === undefined || (Array.isArray(field.value) && field.value.length === 0);
        if (isEmpty) {
          if (!newFieldErrors[posIdx]) newFieldErrors[posIdx] = {};
          newFieldErrors[posIdx][field.id] = `${field.label} is required`;
          hasError = true;
        }
      });
    });
    if (hasError) { setFieldErrors(newFieldErrors); return false; }
    return true;
  };

  // ── Popup openers ─────────────────────────────────────────────────────────

  const openTrackingCfPopup = () => {
    if (!oceanNum.trim()) { toast.error("Please enter at least one tracking number first."); return; }
    setOpenAccordion(0);
    setOceanModal((p) => ({ ...p, trackingCfPopup: true }));
  };

  const handleTrackingCfOk = () => {
    if (!validateEntries()) return;
    setOceanModal((p) => ({ ...p, trackingCfPopup: false }));
  };

  const openCfTabPopup = () => {
    const entries = buildCfAccordionEntries(fieldSets, allField, cfAccordionEntries);
    if (entries.length === 0) { toast.error("Please fill at least one Unique custom field first."); return; }
    setCfAccordionEntries(entries);
    setCfOpenAccordion(0);
    setCfEntryFieldErrors({});
    setCfEntryErrors({});
    setOceanModal((p) => ({ ...p, cfTabPopup: true }));
  };

  const handleCfTabOk = () => {
    if (!validateCfEntries()) return;
    setOceanModal((p) => ({ ...p, cfTabPopup: false }));
  };

  // ── transformAlertConfigData ──────────────────────────────────────────────  ← added

  const transformAlertConfigData = (data: any) => ({
    whatsApp: (data?.whatsApp || []).map((item: { value: string }) => item.value) || [],
    emails: (data?.emails || []).map((item: { value: string }) => item.value) || [],
    whatsAppgroups: (data?.whatsAppgroups || []).map((item: { value: string }) => item.value) || [],
    whatsAppmilestones: data?.whatsAppmilestones || [],
    emailsmilestones: data?.emailsmilestones || [],
    whatsappGroupmilestones: data?.whatsappGroupmilestones || [],
  });

  // ── Payload ───────────────────────────────────────────────────────────────

  const buildOceanPayload = (
    trackingValue: string[] | null,
    customfieldsValue: any[],
    entries: TrackingEntry[]
  ) => ({
    oceanTrackingNumber: trackingValue,
    whatsApps: oceanSubForm.whatsApp?.map((i: any) => i.value),
    emails: oceanSubForm.emails?.map((i: any) => i.value),
    emailMilestones: oceanSubForm.emailMilestones,
    whatsAppMilestones: oceanSubForm.whatsAppMilestones,
    whatsappGroups: oceanSubForm.whatsAppgroups?.map((i: any) => i.value),
    whatsappGroupMilestones: oceanSubForm.whatsappGroupMilestones,
    tag: null,
    groupMilestone: oceanSubForm.groupMilestone?.value || null,
    flags: oceanSubForm.flags?.map((i: any) => i.value),
    contactOrganization: oceanSubForm.contactOrganization?.value || "",
    customfields: customfieldsValue,
    customerName: null,
    trackingEntries: entries.map((entry) => ({
      ...entry,
      customFields: convertArray(entry.customFields),
      customerName: entry.customerName?.item?.name || entry.customerName?.label || null,
      groupMilestone: entry.groupMilestone?.value || null,
      alertConfig: transformAlertConfigData(entry.alertConfig ?? null),  // ← added
    })),
  });

  const resetAll = () => {
    setOceanSubForm((prev: any) => ({ ...prev, ...emptySubscriptionForm }));
    setOceanNum("");
    setTrackingEntries([]);
    setEntryFieldErrors({});
    setEntryErrors({});
    const key = setCounter;
    setFieldSets([buildBlankFieldSet(allField, key)]);
    setSetCounter((c) => c + 1);
    setErrors({});
    setFieldErrors({});
    setCfAccordionEntries([]);
    setCfEntryFieldErrors({});
    setCfEntryErrors({});
    setCfRows([buildEmptyRow(`row_${Date.now()}`)]);
    setCfUniqueErrors({});
    setCfValidating({});
    setApplyAllMilestone(null);
    setApplyAllAlert(null);         // ← added
  };

  // ── Submit ────────────────────────────────────────────────────────────────

  const handleOceanSubscribe = async () => {
    setCanCloseDrawer(false);
    if (inputMode === "tracking") {
      if (!oceanNum.trim()) { toast.error("Please enter at least one tracking number."); return; }
      const trackingList = oceanNum.split(",").map((a) => a.trim()).filter(Boolean);
      const customfieldsValue = trackingEntries.map((e) => convertArray(e.customFields));
      try {
        setIsSubmitting(true);
        const payload = buildOceanPayload(trackingList, customfieldsValue, trackingEntries);
        const res = await axiosInstance.post("/api/v1/ocean/add-ocean-number", payload);
        setOceanSubData(res.data?.data?.success);
        setOceanStatusMsg(res.data?.message);
        if (res.data.success) {
          setOceanErrorList(res.data.data.failed.length > 0 ? res.data.data.failed : null);
          setOceanShow(true);
          if (res.data.data.failed.length === 0) { resetAll(); setCanCloseDrawer(true); }
        }
      } catch (err: any) {
        const errData = err?.response?.data;
        if (errData?.error?.length > 0) {
          const logs = Array.isArray(errData.error)
            ? errData.error.map((e: any) => ({ awb: "", reason: e.message }))
            : [{ awb: "", reason: errData.error }];
          setOceanErrorList(logs);
          setOceanStatusMsg({});
          setOceanShow(true);
        }
      } finally { setIsSubmitting(false); }
    } else {
      if (!validateAllSets()) { toast.error("No entries found. Please fill a Unique field first."); return; }
      if (!hasUnique) { toast.error("Please fill at least one Unique custom field first."); return; }
      const cfTrackingEntries: TrackingEntry[] = cfAccordionEntries.map((entry) => ({
        trackingNumber: entry.identifier,
        customFields: entry.fields,
        customerName: entry.customerName,
        groupMilestone: entry.groupMilestone,
        alertConfig: entry.alertConfig,   // ← added
      }));
      try {
        setIsSubmitting(true);
        const payload = buildOceanPayload(null, fieldSets.map((set) => convertArray(set)), cfTrackingEntries);
        const res = await axiosInstance.post("/api/v1/ocean/add-ocean-number", payload);
        setOceanSubData(res.data?.data?.success);
        setOceanStatusMsg(res.data?.message);
        if (res.data.success) {
          setOceanErrorList(res.data.data.failed.length > 0 ? res.data.data.failed : null);
          setOceanShow(true);
          if (res.data.data.failed.length === 0) { resetAll(); setCanCloseDrawer(true); }
        }
      } catch (err: any) {
        const errData = err?.response?.data;
        if (errData?.error?.length > 0) {
          const logs = Array.isArray(errData.error)
            ? errData.error.map((e: any) => ({ awb: "", reason: e.message }))
            : [{ awb: "", reason: errData.error }];
          setOceanErrorList(logs);
          setOceanStatusMsg({});
          setOceanShow(true);
        }
      } finally { setIsSubmitting(false); }
    }
  };

  // ── Derived UI values ─────────────────────────────────────────────────────

  const oceanConfigured =
    oceanSubForm.whatsAppMilestones?.length > 0 ||
    oceanSubForm.emailMilestones?.length > 0;

  const filledEntriesCount = trackingEntries.filter((e) =>
    e.customFields.some((f) => f.value !== "" && f.value !== undefined && !(Array.isArray(f.value) && f.value.length === 0))
  ).length;
  const trackingCount = trackingEntries.length;

  const cfCount = fieldSets.filter((set) => set[0]?.isUnique && (set[0]?.value ?? "") !== "").length;

  const cfConfiguredCount = cfAccordionEntries.filter((entry) =>
    entry.fields.some((f) => !f.prefilled && f.value !== "" && f.value !== undefined && !(Array.isArray(f.value) && f.value.length === 0))
  ).length;

  const trackingGroupingConfigured = trackingEntries.some((e) => e.groupMilestone);
  const cfGroupingConfigured = cfAccordionEntries.some((e) => e.groupMilestone);

  const milestoneOptions =
    oceanSubForm?.groupingmilestonesList?.map((item: any) => ({
      label: item?.groupName,
      value: item?._id,
      item,
    })) ?? [];

  // ── Alert config badge helpers ────────────────────────────────────────────  ← added
  const isAlertConfigured = (cfg?: AlertConfig) =>
    !!cfg && (cfg.whatsApp.length > 0 || cfg.emails.length > 0 || cfg.whatsAppgroups.length > 0);

  const trackingAlertConfigured = trackingEntries.some((e) => isAlertConfigured(e.alertConfig));
  const cfAlertConfigured = cfAccordionEntries.some((e) => isAlertConfigured(e.alertConfig));
  const trackingAlertCount = trackingEntries.filter((e) => isAlertConfigured(e.alertConfig)).length;
  const cfAlertCount = cfAccordionEntries.filter((e) => isAlertConfigured(e.alertConfig)).length;

  const sharedMilestoneList = oceanSubForm.milestoneList ?? [];

  // ── Render ────────────────────────────────────────────────────────────────

  return (
    <>
      <div className={`atd-panel${activeTab === "ocean" ? " active" : ""}`}>
        {isLowOcean && (
          <div className="atd-low-balance">
            ⚠️ &nbsp;Your account balance is low. Please top up to add new tracking numbers.
          </div>
        )}

        <div className="atd-body-top-main">
          <div className="atd-body-top">

            {/* ── Tab Toggle ── */}
            <div className="atd-mode-toggle">
              <button
                className={`atd-mode-btn${inputMode === "tracking" ? " atd-mode-btn--active" : ""}`}
                onClick={() => handleModeSwitch("tracking")}
              >
                <span className="atd-mode-icon">🔢</span>
                Tracking Number
              </button>
              <button
                className={`atd-mode-btn${inputMode === "customFields" ? " atd-mode-btn--active" : ""}`}
                onClick={() => handleModeSwitch("customFields")}
              >
                <span className="atd-mode-icon">📋</span>
                Custom Fields
              </button>
            </div>

            {/* ════════ TAB: TRACKING NUMBER ════════ */}
            {inputMode === "tracking" && (
              <>
                <div className="atd-section-label">Tracking Number</div>
                <textarea
                  className="atd-textarea"
                  placeholder={"Add tracking number(s)\ne.g. BLXXXXXXXXX-MSCU\nPrefix: BL · BK · CT — separate multiple with commas"}
                  value={oceanNum}
                  onChange={handleOceanInput}
                  disabled={isLowOcean}
                />
                <div className="atd-hint-row">
                  <span className="atd-format-hint">BL · BK · CT + Number - SCAC</span>
                  <button className="atd-airlines-link" onClick={() => setOceanLinesModal(true)}>
                    Available shipping lines ↗
                  </button>
                </div>
              </>
            )}

            {/* ════════ TAB: CUSTOM FIELDS ════════ */}
            {inputMode === "customFields" && (
              <CustomFieldsPanel
                allField={allField}
                fieldSets={fieldSets}
                setFieldSets={setFieldSets}
                setCounter={setCounter}
                setSetCounter={setSetCounter}
                simpleOptions={simpleOptions}
                Fieldstype={Fieldstype}
                errors={errors}
                setErrors={setErrors}
                fieldErrors={fieldErrors}
                setFieldErrors={setFieldErrors}
                setDataArray={setDataArray}
                airSubForm={oceanSubForm}
                setAirSubForm={setOceanSubForm}
                airModal={oceanModal}
                setAirModal={setOceanModal}
                buildBlankFieldSet={buildBlankFieldSet}
                rows={cfRows}
                setRows={setCfRows}
                uniqueErrors={cfUniqueErrors}
                setUniqueErrors={setCfUniqueErrors}
                validating={cfValidating}
                setValidating={setCfValidating}
              />
            )}

            {/* ── Configuration (always visible) ── */}
            <div className="atd-section-label">Configuration</div>
            <div className="atd-config-section">

              {/* ── Alert Triggers — per-entry popup ── */}
              <div className="atd-config-row" onClick={openPerEntryAlertPopup}>
                <div className="atd-config-left">
                  <div className="atd-config-icon">🔔</div>
                  <div>
                    <div className="atd-config-text">Alert Triggers</div>
                    {inputMode === "tracking" && trackingEntries.length > 0 ? (
                      <div className={trackingAlertConfigured ? "atd-badge" : "atd-badge atd-badge--zero"}>
                        {trackingAlertConfigured ? "✓ " : ""}
                        {trackingAlertCount} of {trackingCount} number{trackingCount !== 1 ? "s" : ""} configured
                      </div>
                    ) : inputMode === "customFields" && cfCount > 0 ? (
                      <div className={cfAlertConfigured ? "atd-badge" : "atd-badge atd-badge--zero"}>
                        {cfAlertConfigured ? "✓ " : ""}
                        {cfAlertCount} of {cfCount} entr{cfCount !== 1 ? "ies" : "y"} configured
                      </div>
                    ) : oceanConfigured ? (
                      <div className="atd-badge">
                        ✓ {oceanSubForm.whatsAppMilestones?.length} WA ·{" "}
                        {oceanSubForm.emailMilestones?.length} Email
                      </div>
                    ) : (
                      <div className="atd-config-sub">WhatsApp &amp; Email milestones</div>
                    )}
                  </div>
                </div>
                <span className="atd-config-chevron">›</span>
              </div>

              {/* ── Milestone Grouping ── */}
              <div className="atd-config-row" onClick={openPerEntryGroupingPopup}>
                <div className="atd-config-left">
                  <div className="atd-config-icon">🏁</div>
                  <div>
                    <div className="atd-config-text">Milestone Grouping</div>
                    {inputMode === "tracking" && trackingEntries.length > 0 ? (
                      <div className={trackingGroupingConfigured ? "atd-badge" : "atd-badge atd-badge--zero"}>
                        {trackingGroupingConfigured ? "✓ " : ""}
                        {trackingEntries.filter((e) => e.groupMilestone).length}{" "}
                        of {trackingEntries.length} number{trackingEntries.length !== 1 ? "s" : ""} configured
                      </div>
                    ) : inputMode === "customFields" && cfCount > 0 ? (
                      <div className={cfGroupingConfigured ? "atd-badge" : "atd-badge atd-badge--zero"}>
                        {cfGroupingConfigured ? "✓ " : ""}
                        {cfAccordionEntries.filter((e) => e.groupMilestone).length}{" "}
                        of {cfCount} entr{cfCount !== 1 ? "ies" : "y"} configured
                      </div>
                    ) : oceanSubForm.groupMilestone?.value ? (
                      <div className="atd-badge">✓ {oceanSubForm.groupMilestone?.item?.groupName}</div>
                    ) : (
                      <div className="atd-config-sub">Assign a grouping template</div>
                    )}
                  </div>
                </div>
                <span className="atd-config-chevron">›</span>
              </div>

              {/* Tracking tab: custom fields config row */}
              {inputMode === "tracking" && (
                <div className="atd-config-row" onClick={openTrackingCfPopup}>
                  <div className="atd-config-left">
                    <div className="atd-config-icon">📋</div>
                    <div>
                      <div className="atd-config-text">Custom Fields</div>
                      {trackingCount > 0 ? (
                        <div className="atd-badge">
                          ✓ {filledEntriesCount} of {trackingCount} number{trackingCount !== 1 ? "s" : ""} configured
                        </div>
                      ) : (
                        <div className="atd-config-sub">Add custom field values per tracking number</div>
                      )}
                    </div>
                  </div>
                  <span className="atd-config-chevron">›</span>
                </div>
              )}

              {/* Custom Fields tab: its own config row */}
              {inputMode === "customFields" && (
                <div className="atd-config-row" onClick={openCfTabPopup}>
                  <div className="atd-config-left">
                    <div className="atd-config-icon">📋</div>
                    <div>
                      <div className="atd-config-text">Custom Fields</div>
                      {cfCount > 0 ? (
                        <div className="atd-badge">
                          ✓ {cfConfiguredCount} of {cfCount} entr{cfCount !== 1 ? "ies" : "y"} configured
                        </div>
                      ) : (
                        <div className="atd-config-sub">Review &amp; fill remaining fields per entry</div>
                      )}
                    </div>
                  </div>
                  <span className="atd-config-chevron">›</span>
                </div>
              )}
            </div>
          </div>

          {/* ── Footer ── */}
          <div className="footerDrawerTracking">
            <button className="btn btnYellow" onClick={handleOceanSubscribe} disabled={isLowOcean || isSubmitting}>
              {isSubmitting ? "Adding..." : "Add Tracking"}
            </button>
            <div className="atd-csv-row" onClick={() => setShowAirBulk(true)}>
              <div className="atd-csv-icon">↑</div>
              <div className="atd-csv-text-wrap">
                <div className="atd-csv-title">Bulk CSV Upload</div>
                <div className="atd-csv-desc">Add multiple numbers with custom rules</div>
              </div>
              <span className="atd-csv-chevron">›</span>
            </div>
          </div>
        </div>
      </div>

      {/* ════════════════════════════════════════════════════════════════════
          SHARED MODALS
      ════════════════════════════════════════════════════════════════════ */}

      <OceanShippingShowModal
        open={oceanLinesModal}
        handleClose={() => setOceanLinesModal(false)}
        shippingLines={shippingLines}
      />

      <OceanDialogBox
        open={oceanShow}
        successMessage={
          <DialogTitle>
            <Typography
              variant="h6"
              className={oceanStatusMsg?.messageStatus === 1 ? "text-green-500" : "text-red-500"}
            >
              {oceanStatusMsg?.message}
            </Typography>
          </DialogTitle>
        }
        handleClose={() => {
          setOceanShow(false);
          setOceanErrorList(null);
          canClosedrawer && drawerClose();
        }}
        data={oceanErrorList}
      />


      {/* ════════════════════════════════════════════════════════════════════
          TRACKING TAB — Custom Fields popup
      ════════════════════════════════════════════════════════════════════ */}
      <ModalPopup
        open={oceanModal.trackingCfPopup}
        handleClose={() => { setOceanModal((p) => ({ ...p, trackingCfPopup: false })); setEntryFieldErrors({}); }}
        heading={"Custom Fields"}
      >
        {oceanModal.trackingCfPopup && (
          <div className="atd-cf-modal-body">
            {trackingEntries.length === 0 ? (
              <div className="atd-cf-empty"><span>📋</span><p>No tracking numbers found. Please enter tracking numbers first.</p></div>
            ) : (
              trackingEntries.map((entry, entryIdx) => {
                const isOpen = openAccordion === entryIdx;
                const hasFilledFields = entry.customFields.some(
                  (f) => f.value !== "" && f.value !== undefined && !(Array.isArray(f.value) && f.value.length === 0)
                );
                const hasErrors = !!entryFieldErrors[entryIdx] && Object.keys(entryFieldErrors[entryIdx]).length > 0;
                return (
                  <div key={entry.trackingNumber} className={["atd-cf-accordion", isOpen ? "atd-cf-accordion--open" : "", hasErrors ? "atd-cf-accordion--error" : ""].join(" ").trim()}>
                    <button type="button" className="atd-cf-accordion-header" onClick={() => setOpenAccordion(isOpen ? null : entryIdx)}>
                      <div className="atd-cf-accordion-left">
                        <span className="atd-cf-accordion-num">{entryIdx + 1}</span>
                        <span className="atd-cf-accordion-awb">{entry.trackingNumber}</span>
                        {hasErrors && <span className="atd-cf-accordion-badge atd-cf-accordion-badge--error">⚠ Required fields missing</span>}
                        {!hasErrors && hasFilledFields && <span className="atd-cf-accordion-badge atd-cf-accordion-badge--done">✓ Filled</span>}
                      </div>
                      <span className="atd-cf-accordion-chevron">{isOpen ? "▲" : "▼"}</span>
                    </button>
                    {isOpen && (
                      <div className="atd-cf-accordion-body">
                        <AddcustomfieldsForm
                          setmodalPopup={setOceanModal}
                          fields={entry.customFields}
                          setFields={makeSetFields(entryIdx)}
                          errors={entryErrors[entryIdx] ?? {}}
                          setErrors={makeSetEntryErrors(entryIdx)}
                          setDataArray={setDataArray}
                          simpleOptions={simpleOptions}
                          Fieldstype={Fieldstype}
                          fieldErrors={entryFieldErrors[entryIdx] ?? {}}
                          setFieldErrors={makeSetEntryFieldErrors(entryIdx)}
                          subscriptionFormData={oceanSubForm}
                          setSubscriptionFormData={setOceanSubForm}
                          entryCustomerName={entry.customerName ?? null}
                          onCustomerNameChange={makeSetCustomerName(entryIdx)}
                        />
                      </div>
                    )}
                  </div>
                );
              })
            )}
            <div className="modalInnerFieldFooter flex justify-end" style={{ marginTop: 16 }}>
              <button className="btnTheme btnPadding" onClick={handleTrackingCfOk}>OK</button>
            </div>
          </div>
        )}
      </ModalPopup>

      {/* ════════════════════════════════════════════════════════════════════
          CUSTOM FIELDS TAB — accordion popup
      ════════════════════════════════════════════════════════════════════ */}
      <ModalPopup
        open={oceanModal.cfTabPopup}
        handleClose={() => { setOceanModal((p) => ({ ...p, cfTabPopup: false })); setCfEntryFieldErrors({}); }}
        heading={"Custom Fields"}
      >
        {oceanModal.cfTabPopup && (
          <div className="atd-cf-modal-body">
            {cfAccordionEntries.length === 0 ? (
              <div className="atd-cf-empty"><span>📋</span><p>No entries found. Please fill a Unique field in the panel first.</p></div>
            ) : (
              cfAccordionEntries.map((entry, entryIdx) => {
                const isOpen = cfOpenAccordion === entryIdx;
                const hasFilledFields = entry.fields.some(
                  (f) => !f.prefilled && f.value !== "" && f.value !== undefined && !(Array.isArray(f.value) && f.value.length === 0)
                );
                const hasErrors = !!cfEntryFieldErrors[entryIdx] && Object.keys(cfEntryFieldErrors[entryIdx]).length > 0;
                return (
                  <div key={entry.id} className={["atd-cf-accordion", isOpen ? "atd-cf-accordion--open" : "", hasErrors ? "atd-cf-accordion--error" : ""].join(" ").trim()}>
                    <button type="button" className="atd-cf-accordion-header" onClick={() => setCfOpenAccordion(isOpen ? null : entryIdx)}>
                      <div className="atd-cf-accordion-left">
                        <span className="atd-cf-accordion-num">{entryIdx + 1}</span>
                        <span className="atd-cf-accordion-awb">{entry.identifier}</span>
                        <span style={{ fontSize: 11, color: "#888", marginLeft: 4, fontWeight: 400 }}>{entry.fieldName}</span>
                        {hasErrors && <span className="atd-cf-accordion-badge atd-cf-accordion-badge--error">⚠ Required fields missing</span>}
                        {!hasErrors && hasFilledFields && <span className="atd-cf-accordion-badge atd-cf-accordion-badge--done">✓ Filled</span>}
                      </div>
                      <span className="atd-cf-accordion-chevron">{isOpen ? "▲" : "▼"}</span>
                    </button>
                    {isOpen && (
                      <div className="atd-cf-accordion-body">
                        <AddcustomfieldsForm
                          setmodalPopup={setOceanModal}
                          fields={entry.fields}
                          setFields={makeCfSetFields(entryIdx)}
                          errors={cfEntryErrors[entryIdx] ?? {}}
                          setErrors={makeCfEntryErrors(entryIdx)}
                          setDataArray={setDataArray}
                          simpleOptions={simpleOptions}
                          Fieldstype={Fieldstype}
                          fieldErrors={cfEntryFieldErrors[entryIdx] ?? {}}
                          setFieldErrors={makeCfEntryFieldErrors(entryIdx)}
                          subscriptionFormData={oceanSubForm}
                          setSubscriptionFormData={setOceanSubForm}
                          entryCustomerName={entry.customerName ?? null}
                          onCustomerNameChange={makeCfSetCustomerName(entryIdx)}
                        />
                      </div>
                    )}
                  </div>
                );
              })
            )}
            <div className="modalInnerFieldFooter flex justify-end" style={{ marginTop: 16 }}>
              <button className="btnTheme btnPadding" onClick={handleCfTabOk}>OK</button>
            </div>
          </div>
        )}
      </ModalPopup>

      {/* ════════════════════════════════════════════════════════════════════
          PER-ENTRY MILESTONE GROUPING POPUP
      ════════════════════════════════════════════════════════════════════ */}
      <ModalPopup
        open={oceanModal.perEntryGroupingPopup}
        handleClose={() => setOceanModal((p) => ({ ...p, perEntryGroupingPopup: false }))}
        heading={"Milestone Grouping"}
      >
        {oceanModal.perEntryGroupingPopup && (
          <div className="atd-cf-modal-body">
            <div style={{ background: "#f8f8f8", border: "1px solid #e0e0e0", borderRadius: 8, padding: "12px 14px", marginBottom: 16 }}>
              <div style={{ fontSize: 13, fontWeight: 600, marginBottom: 8, color: "#333", display: "flex", alignItems: "center", gap: 6 }}>
                <span>🏁</span> Apply to All
              </div>
              <Select
                isClearable
                placeholder="Select milestone grouping for all entries..."
                options={milestoneOptions}
                value={applyAllMilestone}
                onChange={(option) => handleApplyAllMilestone(option)}
                menuPortalTarget={document.body}
                menuPosition="fixed"
                styles={{
                  control: (base) => ({ ...base, backgroundColor: "#fff", borderColor: "#ddd", boxShadow: "none", "&:hover": { borderColor: "#bbb" } }),
                  placeholder: (base) => ({ ...base, color: "#999" }),
                  menuPortal: (base) => ({ ...base, zIndex: 9999 }),
                }}
              />
              {applyAllMilestone && (
                <div style={{ fontSize: 11, color: "#666", marginTop: 6, display: "flex", alignItems: "center", gap: 4 }}>
                  <span style={{ color: "#22c55e" }}>✓</span>
                  Applied to all entries. You can still override individually below — doing so will clear the &quot;Apply to All&quot; selection.
                </div>
              )}
            </div>

            {inputMode === "tracking" ? (
              trackingEntries.length === 0 ? (
                <div className="atd-cf-empty"><span>🏁</span><p>No tracking numbers found. Please enter tracking numbers first.</p></div>
              ) : (
                trackingEntries.map((entry, entryIdx) => {
                  const isOpen = groupingOpenAccordion === entryIdx;
                  const milestone = entry.groupMilestone;
                  return (
                    <div key={entry.trackingNumber} className={["atd-cf-accordion", isOpen ? "atd-cf-accordion--open" : ""].join(" ").trim()}>
                      <button type="button" className="atd-cf-accordion-header" onClick={() => setGroupingOpenAccordion(isOpen ? null : entryIdx)}>
                        <div className="atd-cf-accordion-left">
                          <span className="atd-cf-accordion-num">{entryIdx + 1}</span>
                          <span className="atd-cf-accordion-awb">{entry.trackingNumber}</span>
                          {milestone ? (
                            <span className="atd-cf-accordion-badge atd-cf-accordion-badge--done">✓ {milestone.item?.groupName ?? milestone.label}</span>
                          ) : (
                            <span style={{ fontSize: 11, color: "#aaa", marginLeft: 6 }}>Not set</span>
                          )}
                        </div>
                        <span className="atd-cf-accordion-chevron">{isOpen ? "▲" : "▼"}</span>
                      </button>
                      {isOpen && (
                        <div className="atd-cf-accordion-body">
                          <MilestoneGroupingAccordionBody milestoneOptions={milestoneOptions} value={entry.groupMilestone ?? null} onChange={makeSetGroupMilestone(entryIdx)} />
                        </div>
                      )}
                    </div>
                  );
                })
              )
            ) : cfAccordionEntries.length === 0 ? (
              <div className="atd-cf-empty"><span>🏁</span><p>No entries found. Please fill a Unique field in the panel first.</p></div>
            ) : (
              cfAccordionEntries.map((entry, entryIdx) => {
                const isOpen = groupingOpenAccordion === entryIdx;
                const milestone = entry.groupMilestone;
                return (
                  <div key={entry.id} className={["atd-cf-accordion", isOpen ? "atd-cf-accordion--open" : ""].join(" ").trim()}>
                    <button type="button" className="atd-cf-accordion-header" onClick={() => setGroupingOpenAccordion(isOpen ? null : entryIdx)}>
                      <div className="atd-cf-accordion-left">
                        <span className="atd-cf-accordion-num">{entryIdx + 1}</span>
                        <span className="atd-cf-accordion-awb">{entry.identifier}</span>
                        <span style={{ fontSize: 11, color: "#888", marginLeft: 4, fontWeight: 400 }}>{entry.fieldName}</span>
                        {milestone ? (
                          <span className="atd-cf-accordion-badge atd-cf-accordion-badge--done">✓ {milestone.item?.groupName ?? milestone.label}</span>
                        ) : (
                          <span style={{ fontSize: 11, color: "#aaa", marginLeft: 6 }}>Not set</span>
                        )}
                      </div>
                      <span className="atd-cf-accordion-chevron">{isOpen ? "▲" : "▼"}</span>
                    </button>
                    {isOpen && (
                      <div className="atd-cf-accordion-body">
                        <MilestoneGroupingAccordionBody milestoneOptions={milestoneOptions} value={entry.groupMilestone ?? null} onChange={makeCfSetGroupMilestone(entryIdx)} />
                      </div>
                    )}
                  </div>
                );
              })
            )}

            <div className="modalInnerFieldFooter flex justify-end" style={{ marginTop: 16 }}>
              <button className="btnTheme btnPadding" onClick={() => setOceanModal((p) => ({ ...p, perEntryGroupingPopup: false }))}>OK</button>
            </div>
          </div>
        )}
      </ModalPopup>

      {/* ════════════════════════════════════════════════════════════════════
          PER-ENTRY ALERT TRIGGERS POPUP                          ← added
      ════════════════════════════════════════════════════════════════════ */}
      <ModalPopup
        open={oceanModal.perEntryAlertPopup}
        handleClose={() => setOceanModal((p) => ({ ...p, perEntryAlertPopup: false }))}
        heading={"Alert Triggers"}
      >
        {oceanModal.perEntryAlertPopup && (
          <div className="atd-cf-modal-body">

            {/* Apply to All banner */}
            <div style={{ background: "#f8f8f8", border: "1px solid #e0e0e0", borderRadius: 8, padding: "12px 14px", marginBottom: 16 }}>
              <div style={{ fontSize: 13, fontWeight: 600, marginBottom: 10, color: "#333", display: "flex", alignItems: "center", gap: 6 }}>
                <span>🔔</span> Apply to All Entries
              </div>
              <AlertConfigAccordionBody
                value={applyAllAlert ?? EMPTY_ALERT_CONFIG}
                onChange={(cfg) => handleApplyAllAlert(cfg)}
                allContactList={sharedContactList}
                templateList={sharedTemplateList}
                organisationList={sharedOrgList}
                whatsappGroupList={oceanSubForm.whatsappGroupList ?? []}
                milestoneList={sharedMilestoneList}
              />
              {applyAllAlert && isAlertConfigured(applyAllAlert) && (
                <div style={{ fontSize: 11, color: "#666", marginTop: 8, display: "flex", alignItems: "center", gap: 4 }}>
                  <span style={{ color: "#22c55e" }}>✓</span>
                  Applied to all entries. You can still override individually below — doing so will clear the &quot;Apply to All&quot; selection.
                </div>
              )}
            </div>

            {/* Per-entry accordions */}
            {inputMode === "tracking" ? (
              trackingEntries.length === 0 ? (
                <div className="atd-cf-empty"><span>🔔</span><p>No tracking numbers found. Please enter tracking numbers first.</p></div>
              ) : (
                trackingEntries.map((entry, entryIdx) => {
                  const isOpen = alertOpenAccordion === entryIdx;
                  const cfg = entry.alertConfig;
                  const hasAny = isAlertConfigured(cfg);
                  return (
                    <div key={entry.trackingNumber} className={["atd-cf-accordion", isOpen ? "atd-cf-accordion--open" : ""].join(" ").trim()}>
                      <button type="button" className="atd-cf-accordion-header" onClick={() => setAlertOpenAccordion(isOpen ? null : entryIdx)}>
                        <div className="atd-cf-accordion-left">
                          <span className="atd-cf-accordion-num">{entryIdx + 1}</span>
                          <span className="atd-cf-accordion-awb">{entry.trackingNumber}</span>
                          {hasAny ? (
                            <span className="atd-cf-accordion-badge atd-cf-accordion-badge--done">
                              ✓{cfg!.whatsApp.length > 0 ? ` ${cfg!.whatsApp.length} WA` : ""}
                              {cfg!.emails.length > 0 ? ` · ${cfg!.emails.length} Email` : ""}
                              {cfg!.whatsAppgroups.length > 0 ? ` · ${cfg!.whatsAppgroups.length} Group` : ""}
                            </span>
                          ) : (
                            <span style={{ fontSize: 11, color: "#aaa", marginLeft: 6 }}>Not configured</span>
                          )}
                        </div>
                        <span className="atd-cf-accordion-chevron">{isOpen ? "▲" : "▼"}</span>
                      </button>
                      {isOpen && (
                        <div className="atd-cf-accordion-body">
                          <AlertConfigAccordionBody
                            value={entry.alertConfig ?? EMPTY_ALERT_CONFIG}
                            onChange={makeSetAlertConfig(entryIdx)}
                            allContactList={sharedContactList}
                            templateList={sharedTemplateList}
                            organisationList={sharedOrgList}
                            whatsappGroupList={oceanSubForm.whatsappGroupList ?? []}
                            milestoneList={sharedMilestoneList}
                          />
                        </div>
                      )}
                    </div>
                  );
                })
              )
            ) : cfAccordionEntries.length === 0 ? (
              <div className="atd-cf-empty"><span>🔔</span><p>No entries found. Please fill a Unique field in the panel first.</p></div>
            ) : (
              cfAccordionEntries.map((entry, entryIdx) => {
                const isOpen = alertOpenAccordion === entryIdx;
                const cfg = entry.alertConfig;
                const hasAny = isAlertConfigured(cfg);
                return (
                  <div key={entry.id} className={["atd-cf-accordion", isOpen ? "atd-cf-accordion--open" : ""].join(" ").trim()}>
                    <button type="button" className="atd-cf-accordion-header" onClick={() => setAlertOpenAccordion(isOpen ? null : entryIdx)}>
                      <div className="atd-cf-accordion-left">
                        <span className="atd-cf-accordion-num">{entryIdx + 1}</span>
                        <span className="atd-cf-accordion-awb">{entry.identifier}</span>
                        <span style={{ fontSize: 11, color: "#888", marginLeft: 4, fontWeight: 400 }}>{entry.fieldName}</span>
                        {hasAny ? (
                          <span className="atd-cf-accordion-badge atd-cf-accordion-badge--done">
                            ✓{cfg!.whatsApp.length > 0 ? ` ${cfg!.whatsApp.length} WA` : ""}
                            {cfg!.emails.length > 0 ? ` · ${cfg!.emails.length} Email` : ""}
                            {cfg!.whatsAppgroups.length > 0 ? ` · ${cfg!.whatsAppgroups.length} Group` : ""}
                          </span>
                        ) : (
                          <span style={{ fontSize: 11, color: "#aaa", marginLeft: 6 }}>Not configured</span>
                        )}
                      </div>
                      <span className="atd-cf-accordion-chevron">{isOpen ? "▲" : "▼"}</span>
                    </button>
                    {isOpen && (
                      <div className="atd-cf-accordion-body">
                        <AlertConfigAccordionBody
                          value={entry.alertConfig ?? EMPTY_ALERT_CONFIG}
                          onChange={makeCfSetAlertConfig(entryIdx)}
                          allContactList={sharedContactList}
                          templateList={sharedTemplateList}
                          organisationList={sharedOrgList}
                          whatsappGroupList={oceanSubForm.whatsappGroupList ?? []}
                          milestoneList={sharedMilestoneList}
                        />
                      </div>
                    )}
                  </div>
                );
              })
            )}

            <div className="modalInnerFieldFooter flex justify-end" style={{ marginTop: 16 }}>
              <button className="btnTheme btnPadding" onClick={() => setOceanModal((p) => ({ ...p, perEntryAlertPopup: false }))}>OK</button>
            </div>
          </div>
        )}
      </ModalPopup>
    </>
  );
}